import { useState, useCallback, useMemo, useEffect } from 'react'
import { useAuth } from '@/modules/auth'
import { AnnouncementsService } from '../services/announcementsService'
import type {
  TripAnnouncement,
  CreateAnnouncementDto,
  UpdateAnnouncementDto,
} from '@/shared/api/types'

export function useTripAnnouncements(tripId: string | undefined) {
  const { getAccessToken } = useAuth()
  const [announcements, setAnnouncements] = useState<TripAnnouncement[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const service = useMemo(
    () => new AnnouncementsService(getAccessToken),
    [getAccessToken]
  )

  const load = useCallback(async () => {
    if (!tripId) return

    setIsLoading(true)
    setError(null)

    try {
      const data = await service.getTripAnnouncements(tripId)
      setAnnouncements(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load announcements')
    } finally {
      setIsLoading(false)
    }
  }, [tripId, service])

  useEffect(() => {
    load()
  }, [load])

  const createAnnouncement = useCallback(
    async (dto: CreateAnnouncementDto) => {
      if (!tripId) throw new Error('Trip ID is required')
      const created = await service.createAnnouncement(tripId, dto)
      setAnnouncements((prev) => [created, ...prev])
      return created
    },
    [tripId, service]
  )

  const updateAnnouncement = useCallback(
    async (id: string, dto: UpdateAnnouncementDto) => {
      const updated = await service.updateAnnouncement(id, dto)
      setAnnouncements((prev) =>
        prev.map((a) => (a.id === id ? updated : a))
      )
      return updated
    },
    [service]
  )

  const deleteAnnouncement = useCallback(
    async (id: string) => {
      await service.deleteAnnouncement(id)
      setAnnouncements((prev) => prev.filter((a) => a.id !== id))
    },
    [service]
  )

  return {
    announcements,
    isLoading,
    error,
    refetch: load,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
  }
}
